'use client';
import React, { useState, useCallback, useRef } from 'react';
import Link from 'next/link';



// ─── Types ───────────────────────────────────────────────────────────────────
interface StatCard {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  icon: React.ReactNode;
  accent: string;
}

interface Event {
  id: number;
  title: string;
  date: string;
  venue: string;
  status: 'Live' | 'Upcoming' | 'Completed' | 'Draft';
  tickets: number;
  revenue: string;
}

interface Member {
  id: number;
  name: string;
  role: string;
  joined: string;
  status: 'Active' | 'Pending' | 'Inactive';
  tier: string;
}

interface Activity {
  id: number;
  action: string;
  subject: string;
  time: string;
  type: 'event' | 'member' | 'revenue' | 'system';
}

interface PageField {
  key: string;
  label: string;
  value: string;
  type: 'text' | 'textarea' | 'url' | 'image' | 'video' | 'color' | 'number';
}

interface PageSection {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: PageField[];
  visible: boolean;
  locked?: boolean;
}

interface AppPage {
  id: string;
  name: string;
  route: string;
  icon: string;
  sections: PageSection[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const stats: StatCard[] = [
  {
    label: 'Total Events', value: '148', change: '+12 this month', positive: true, accent: '#C9A84C',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#C9A84C" strokeWidth="1.8" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 14h2m2 0h2M8 18h2" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Active Members', value: '3,842', change: '+284 this month', positive: true, accent: '#B8860B',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="7" r="4" stroke="#B8860B" strokeWidth="1.8" />
        <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="#B8860B" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.87" stroke="#B8860B" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Revenue (₹)', value: '₹24.7L', change: '+18.4% vs last month', positive: true, accent: '#8B6914',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="#8B6914" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Tickets Sold', value: '18,290', change: '-3.2% vs last month', positive: false, accent: '#9B2335',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M2 9a1 1 0 011-1h18a1 1 0 011 1v2a2 2 0 000 4v2a1 1 0 01-1 1H3a1 1 0 01-1-1v-2a2 2 0 000-4V9z" stroke="#9B2335" strokeWidth="1.8" />
        <path d="M9 12h6" stroke="#9B2335" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="2 2" />
      </svg>
    ),
  },
];

const events: Event[] = [
  { id: 1, title: 'Rani Kamlapati Heritage Gala', date: '15 Sep 2026', venue: 'Grand Durbar Hall', status: 'Live', tickets: 420, revenue: '₹8.4L' },
  { id: 2, title: 'Classical Music Soirée', date: '22 Sep 2026', venue: 'Zenith Pavilion', status: 'Upcoming', tickets: 180, revenue: '₹2.7L' },
  { id: 3, title: 'Royal Craft Exhibition', date: '01 Oct 2026', venue: 'Heritage Gallery', status: 'Upcoming', tickets: 95, revenue: '₹95K' },
  { id: 4, title: 'Monsoon Cultural Fest', date: '10 Aug 2026', venue: 'Open Amphitheatre', status: 'Completed', tickets: 1200, revenue: '₹14.4L' },
  { id: 5, title: 'Bhopal Food Heritage Walk', date: '28 Sep 2026', venue: 'Old City Quarter', status: 'Draft', tickets: 0, revenue: '—' },
  { id: 6, title: 'Nawabi Cuisine Night', date: '05 Oct 2026', venue: 'Palace Courtyard', status: 'Upcoming', tickets: 60, revenue: '₹1.2L' },
];

const members: Member[] = [
  { id: 1, name: 'Priya Sharma', role: 'Heritage Patron', joined: 'Jan 2026', status: 'Active', tier: 'Gold' },
  { id: 2, name: 'Arjun Mehta', role: 'Event Curator', joined: 'Mar 2026', status: 'Active', tier: 'Silver' },
  { id: 3, name: 'Sunita Rao', role: 'Cultural Ambassador', joined: 'May 2026', status: 'Pending', tier: 'Bronze' },
  { id: 4, name: 'Vikram Singh', role: 'Heritage Patron', joined: 'Feb 2026', status: 'Active', tier: 'Platinum' },
  { id: 5, name: 'Meera Joshi', role: 'Heritage Volunteer', joined: 'Jun 2026', status: 'Inactive', tier: 'Bronze' },
];

const activities: Activity[] = [
  { id: 1, action: 'New event published', subject: 'Rani Kamlapati Heritage Gala', time: '2 min ago', type: 'event' },
  { id: 2, action: 'Member registered', subject: 'Priya Sharma joined as Heritage Patron', time: '18 min ago', type: 'member' },
  { id: 3, action: 'Payment received', subject: '₹42,000 from ticket sales', time: '45 min ago', type: 'revenue' },
  { id: 4, action: 'Event updated', subject: 'Classical Music Soirée — venue changed', time: '1 hr ago', type: 'event' },
  { id: 5, action: 'New member request', subject: 'Sunita Rao — pending approval', time: '2 hr ago', type: 'member' },
  { id: 6, action: 'System backup', subject: 'Automated backup completed', time: '3 hr ago', type: 'system' },
];

// ─── Pages Data ───────────────────────────────────────────────────────────────
const buildInitialPages = (): AppPage[] => [
  {
    id: 'home', name: 'Home', route: '/', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    sections: [
      {
        id: 'hero', name: 'Hero Slider', description: 'Main hero banner with slides', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', visible: true,
        fields: [
          { key: 'slide1_title', label: 'Slide 1 Title', value: 'Rani Kamlapati Heritage Festival', type: 'text' },
          { key: 'slide1_subtitle', label: 'Slide 1 Subtitle', value: "Celebrating the Legacy of Bhopal's Royal Heritage", type: 'textarea' },
          { key: 'slide1_image', label: 'Slide 1 Image Upload', value: '', type: 'image' },
          { key: 'slide1_image_link', label: 'Slide 1 Image Link (URL)', value: '', type: 'url' },
          { key: 'slide1_video_link', label: 'Slide 1 Video Link (URL)', value: '', type: 'video' },
          { key: 'slide2_title', label: 'Slide 2 Title', value: 'Cultural Evenings & Grand Galas', type: 'text' },
          { key: 'slide2_subtitle', label: 'Slide 2 Subtitle', value: 'Experience the grandeur of Nawabi traditions', type: 'textarea' },
          { key: 'slide2_image', label: 'Slide 2 Image Upload', value: '', type: 'image' },
          { key: 'slide2_image_link', label: 'Slide 2 Image Link (URL)', value: '', type: 'url' },
          { key: 'slide2_video_link', label: 'Slide 2 Video Link (URL)', value: '', type: 'video' },
        ],
      },
      {
        id: 'events', name: 'Event Grid', description: 'Featured events listing section', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'Upcoming Events', type: 'text' },
          { key: 'section_subtitle', label: 'Section Subtitle', value: 'Discover curated heritage experiences', type: 'textarea' },
          { key: 'cta_label', label: 'CTA Button Label', value: 'View All Events', type: 'text' },
        ],
      },
      {
        id: 'featured', name: 'Featured Banner', description: 'EventSphere premium stats banner', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z', visible: true,
        fields: [
          { key: 'headline', label: 'Headline', value: "India's Premier Heritage Event Platform", type: 'text' },
          { key: 'stat1_label', label: 'Stat 1 Label', value: 'Events Hosted', type: 'text' },
          { key: 'stat1_value', label: 'Stat 1 Value', value: '1,200+', type: 'text' },
          { key: 'stat2_label', label: 'Stat 2 Label', value: 'Members', type: 'text' },
          { key: 'stat2_value', label: 'Stat 2 Value', value: '50,000+', type: 'text' },
        ],
      },
      {
        id: 'testimonials', name: 'Testimonials', description: 'Auto-sliding patron testimonials carousel', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'What Our Patrons Say', type: 'text' },
          { key: 'section_subtitle', label: 'Section Subtitle', value: 'Voices from our esteemed heritage community', type: 'textarea' },
          { key: 'autoplay_interval', label: 'Autoplay Interval (ms)', value: '4000', type: 'number' },
        ],
      },
      {
        id: 'supported_by', name: 'Supported By', description: 'Scrolling logos of supporters and partners', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'Supported By', type: 'text' },
          { key: 'section_subtitle', label: 'Section Subtitle', value: 'Proudly backed by heritage institutions', type: 'textarea' },
        ],
      },
      {
        id: 'serving', name: 'Serving with Purpose', description: 'Mission pillars bento section', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'Serving with Purpose', type: 'text' },
          { key: 'pillar1_title', label: 'Pillar 1 Title', value: 'Cultural Preservation', type: 'text' },
          { key: 'pillar1_desc', label: 'Pillar 1 Description', value: 'Safeguarding centuries of Madhya Pradesh heritage', type: 'textarea' },
          { key: 'pillar2_title', label: 'Pillar 2 Title', value: 'Community Engagement', type: 'text' },
          { key: 'pillar2_desc', label: 'Pillar 2 Description', value: 'Connecting patrons with authentic cultural experiences', type: 'textarea' },
        ],
      },
      {
        id: 'join_us', name: 'Join Us', description: 'Membership roles and CTA section', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'Join Our Heritage Community', type: 'text' },
          { key: 'section_subtitle', label: 'Section Subtitle', value: 'Choose your role in preserving our legacy', type: 'textarea' },
          { key: 'cta_label', label: 'CTA Button Label', value: 'Become a Member', type: 'text' },
        ],
      },
      {
        id: 'news', name: 'News Section', description: 'Latest news and updates', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z', visible: true,
        fields: [
          { key: 'section_title', label: 'Section Title', value: 'Latest News & Updates', type: 'text' },
          { key: 'section_subtitle', label: 'Section Subtitle', value: 'Stay informed about heritage events and announcements', type: 'textarea' },
        ],
      },
      {
        id: 'footer', name: 'Footer', description: 'Site footer with links and info', icon: 'M4 6h16M4 12h16M4 18h7', visible: true, locked: true,
        fields: [
          { key: 'tagline', label: 'Tagline', value: 'Preserving Heritage, Celebrating Culture', type: 'text' },
          { key: 'copyright', label: 'Copyright Text', value: '© 2026 Rani Kamlapati Heritage Platform. All rights reserved.', type: 'text' },
          { key: 'contact_email', label: 'Contact Email', value: 'contact@ranikamlapati.in', type: 'text' },
          { key: 'contact_phone', label: 'Contact Phone', value: '+91 755 000 0000', type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'login', name: 'Login', route: '/login', icon: 'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1',
    sections: [
      {
        id: 'login_form', name: 'Login Form', description: 'Sign in form section', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', visible: true,
        fields: [
          { key: 'page_title', label: 'Page Title', value: 'Welcome Back', type: 'text' },
          { key: 'page_subtitle', label: 'Page Subtitle', value: 'Sign in to your heritage account', type: 'textarea' },
          { key: 'email_placeholder', label: 'Email Placeholder', value: 'Enter your email address', type: 'text' },
          { key: 'password_placeholder', label: 'Password Placeholder', value: 'Enter your password', type: 'text' },
          { key: 'submit_label', label: 'Submit Button Label', value: 'Sign In', type: 'text' },
          { key: 'register_link_text', label: 'Register Link Text', value: "Don't have an account? Register", type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'navbar', name: 'Navbar (Global)', route: 'global', icon: 'M4 6h16M4 12h16M4 18h16',
    sections: [
      {
        id: 'nav_links', name: 'Navigation Links', description: 'Top navigation menu items', icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1', visible: true,
        fields: [
          { key: 'link1', label: 'Nav Link 1', value: 'Events', type: 'text' },
          { key: 'link2', label: 'Nav Link 2', value: 'Awards', type: 'text' },
          { key: 'link3', label: 'Nav Link 3', value: 'Attractions', type: 'text' },
          { key: 'link4', label: 'Nav Link 4', value: 'Exhibitors', type: 'text' },
          { key: 'link5', label: 'Nav Link 5', value: 'About Rani Kamlapati', type: 'text' },
        ],
      },
      {
        id: 'nav_cta', name: 'CTA Buttons', description: 'Navbar call-to-action buttons', icon: 'M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5', visible: true,
        fields: [
          { key: 'become_member_label', label: 'Become Member Button', value: 'Become Member', type: 'text' },
          { key: 'sign_in_label', label: 'Sign In Button', value: 'Sign In', type: 'text' },
          { key: 'store_label', label: 'Store Button', value: 'Store', type: 'text' },
        ],
      },
    ],
  },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    Live:      { bg: 'rgba(34,197,94,0.12)',   color: '#16a34a' },
    Upcoming:  { bg: 'rgba(201,168,76,0.15)',  color: '#92700A' },
    Completed: { bg: 'rgba(100,116,139,0.12)', color: '#475569' },
    Draft:     { bg: 'rgba(155,35,53,0.1)',    color: '#9B2335' },
    Active:    { bg: 'rgba(34,197,94,0.12)',   color: '#16a34a' },
    Pending:   { bg: 'rgba(201,168,76,0.15)',  color: '#92700A' },
    Inactive:  { bg: 'rgba(100,116,139,0.12)', color: '#475569' },
  };
  const s = map[status] ?? { bg: 'rgba(201,168,76,0.15)', color: '#92700A' };
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: s.bg, color: s.color, letterSpacing: '0.3px' }}>
      {status}
    </span>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const map: Record<string, string> = { Platinum: '#64748b', Gold: '#92700A', Silver: '#475569', Bronze: '#9a5c2a' };
  return <span className="text-xs font-semibold" style={{ color: map[tier] ?? '#92700A' }}>{tier}</span>;
}

function ActivityIcon({ type }: { type: Activity['type'] }) {
  const icons: Record<Activity['type'], React.ReactNode> = {
    event: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="#92700A" strokeWidth="2" /><path d="M16 2v4M8 2v4M3 10h18" stroke="#92700A" strokeWidth="2" strokeLinecap="round" /></svg>,
    member: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#B8860B" strokeWidth="2" /><path d="M4 20v-1a8 8 0 0116 0v1" stroke="#B8860B" strokeWidth="2" strokeLinecap="round" /></svg>,
    revenue: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" /></svg>,
    system: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="#64748b" strokeWidth="2" /><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="#64748b" strokeWidth="2" strokeLinecap="round" /></svg>,
  };
  const bg: Record<Activity['type'], string> = { event: 'rgba(201,168,76,0.12)', member: 'rgba(184,134,11,0.1)', revenue: 'rgba(34,197,94,0.1)', system: 'rgba(100,116,139,0.1)' };
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: bg[type] }}>
      {icons[type]}
    </div>
  );
}

// ─── SVG Icon Helper ──────────────────────────────────────────────────────────
function Icon({ path, size = 16, color = 'currentColor', strokeWidth = 1.8 }: { path: string; size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d={path} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Field Editor ─────────────────────────────────────────────────────────────
function FieldEditor({ field, value, onChange }: { field: PageField; value: string; onChange: (v: string) => void }) {
  const inputBase = {
    background: '#FFFFFF',
    border: '1px solid #E0D4B0',
    color: '#2D2010',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    transition: 'border-color 0.15s',
  };

  if (field.type === 'textarea') {
    return (
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 resize-none"
        style={inputBase}
        onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
        onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
      />
    );
  }

  if (field.type === 'image') {
    return (
      <div>
        {value ? (
          <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid #E0D4B0' }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={value} alt="Preview" className="w-full object-cover" style={{ maxHeight: '120px' }} />
            <button
              onClick={() => onChange('')}
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold"
              style={{ background: 'rgba(220,38,38,0.85)', color: '#fff' }}
            >×</button>
          </div>
        ) : (
          <label
            className="flex flex-col items-center justify-center gap-1.5 py-5 rounded-lg cursor-pointer transition-all"
            style={{ border: '1.5px dashed #D4C090', background: '#FAF7F2', color: '#B8A070' }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#D4C090')}
          >
            <Icon path="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={22} color="#C9A84C" />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>Click to upload image</span>
            <span style={{ fontSize: '10px', color: '#C8B880' }}>PNG, JPG, WEBP</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (ev) => onChange(ev.target?.result as string);
                reader.readAsDataURL(file);
              }
            }} />
          </label>
        )}
      </div>
    );
  }

  if (field.type === 'video') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(201,168,76,0.1)', color: '#92700A' }}>
          <Icon path="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" size={14} color="#92700A" />
        </div>
        <input type="url" placeholder="https://youtube.com/watch?v=..." value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2" style={inputBase}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')} />
      </div>
    );
  }

  if (field.type === 'url') {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(201,168,76,0.1)', color: '#92700A' }}>
          <Icon path="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71 M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" size={14} color="#92700A" />
        </div>
        <input type="url" placeholder="https://..." value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2" style={inputBase}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')} />
      </div>
    );
  }

  if (field.type === 'color') {
    return (
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#C9A84C'} onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0.5" style={{ background: '#FAF7F2', border: '1px solid #E0D4B0' }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2" style={inputBase}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')} />
      </div>
    );
  }

  return (
    <input type={field.type === 'number' ? 'number' : 'text'} value={value} onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2" style={inputBase}
      onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
      onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')} />
  );
}

// ─── Add Section Modal ────────────────────────────────────────────────────────
const sectionTemplates = [
  { id: 'custom_hero', name: 'Hero Banner', description: 'Full-width hero with title and CTA', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', fields: [{ key: 'title', label: 'Title', value: 'New Hero Title', type: 'text' as const }, { key: 'subtitle', label: 'Subtitle', value: 'Hero subtitle text', type: 'textarea' as const }, { key: 'cta', label: 'CTA Label', value: 'Get Started', type: 'text' as const }, { key: 'bg_image', label: 'Background Image', value: '', type: 'image' as const }] },
  { id: 'custom_text', name: 'Text Block', description: 'Rich text content section', icon: 'M4 6h16M4 10h16M4 14h10M4 18h6', fields: [{ key: 'heading', label: 'Heading', value: 'Section Heading', type: 'text' as const }, { key: 'body', label: 'Body Text', value: 'Enter your content here...', type: 'textarea' as const }] },
  { id: 'custom_cta', name: 'Call to Action', description: 'Prominent CTA with button', icon: 'M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5', fields: [{ key: 'headline', label: 'Headline', value: 'Ready to get started?', type: 'text' as const }, { key: 'subtext', label: 'Subtext', value: 'Join thousands of satisfied members', type: 'textarea' as const }, { key: 'btn_label', label: 'Button Label', value: 'Join Now', type: 'text' as const }, { key: 'btn_link', label: 'Button Link', value: '/', type: 'url' as const }] },
  { id: 'custom_gallery', name: 'Image Gallery', description: 'Grid of images with captions', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z', fields: [{ key: 'title', label: 'Gallery Title', value: 'Our Gallery', type: 'text' as const }, { key: 'image1', label: 'Image 1', value: '', type: 'image' as const }, { key: 'image2', label: 'Image 2', value: '', type: 'image' as const }, { key: 'image3', label: 'Image 3', value: '', type: 'image' as const }] },
  { id: 'custom_stats', name: 'Stats Row', description: 'Key metrics and numbers', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', fields: [{ key: 'stat1_num', label: 'Stat 1 Number', value: '1,000+', type: 'text' as const }, { key: 'stat1_label', label: 'Stat 1 Label', value: 'Events', type: 'text' as const }, { key: 'stat2_num', label: 'Stat 2 Number', value: '50K+', type: 'text' as const }, { key: 'stat2_label', label: 'Stat 2 Label', value: 'Members', type: 'text' as const }] },
  { id: 'custom_contact', name: 'Contact Info', description: 'Contact details and map embed', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', fields: [{ key: 'email', label: 'Email', value: 'contact@example.com', type: 'text' as const }, { key: 'phone', label: 'Phone', value: '+91 000 000 0000', type: 'text' as const }, { key: 'address', label: 'Address', value: 'Enter address here', type: 'textarea' as const }] },
];

function AddSectionModal({ onAdd, onClose }: { onAdd: (template: typeof sectionTemplates[0]) => void; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const filtered = sectionTemplates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}>
      <div className="rounded-2xl overflow-hidden flex flex-col" style={{ width: '520px', maxHeight: '80vh', background: '#FFFFFF', boxShadow: '0 24px 64px rgba(0,0,0,0.18)', border: '1px solid #E8DFC8' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #F0E8D5' }}>
          <div>
            <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '18px', fontWeight: 700 }}>Add New Section</h3>
            <p style={{ color: '#A08040', fontSize: '12px', marginTop: '2px' }}>Choose a section template to add to your page</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(201,168,76,0.1)', color: '#92700A' }}>
            <Icon path="M6 18L18 6M6 6l12 12" size={14} color="#92700A" />
          </button>
        </div>
        {/* Search */}
        <div className="px-6 py-3" style={{ borderBottom: '1px solid #F0E8D5' }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: '#FAF7F2', border: '1px solid #E0D4B0' }}>
            <Icon path="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={14} color="#B8A070" />
            <input type="text" placeholder="Search templates..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none" style={{ color: '#2D2010', fontSize: '13px' }} />
          </div>
        </div>
        {/* Templates */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            {filtered.map((t) => (
              <button key={t.id} onClick={() => onAdd(t)}
                className="flex flex-col gap-2.5 p-4 rounded-xl text-left transition-all group"
                style={{ background: '#FAF7F2', border: '1px solid #E8DFC8' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.08)'; e.currentTarget.style.borderColor = '#C9A84C'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FAF7F2'; e.currentTarget.style.borderColor = '#E8DFC8'; }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)' }}>
                  <Icon path={t.icon} size={16} color="#92700A" />
                </div>
                <div>
                  <p style={{ color: '#2D2010', fontSize: '13px', fontWeight: 600 }}>{t.name}</p>
                  <p style={{ color: '#A08040', fontSize: '11px', marginTop: '2px', lineHeight: 1.4 }}>{t.description}</p>
                </div>
                <div className="flex items-center gap-1 mt-auto" style={{ color: '#C9A84C', fontSize: '11px', fontWeight: 500 }}>
                  <Icon path="M12 4v16m8-8H4" size={11} color="#C9A84C" />
                  Add section
                </div>
              </button>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-8" style={{ color: '#B8A070', fontSize: '13px' }}>No templates match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Advanced Page Editor ─────────────────────────────────────────────────────
function AdvancedPageEditor({ pages: initialPagesData, onBack }: { pages: AppPage[]; onBack: () => void }) {
  const [pages, setPages] = useState<AppPage[]>(initialPagesData);
  const [selectedPageId, setSelectedPageId] = useState<string>(initialPagesData[0]?.id ?? '');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<Record<string, Record<string, string>>>({});
  const [savedSections, setSavedSections] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saveAllFlash, setSaveAllFlash] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const selectedPage = pages.find((p) => p.id === selectedPageId);
  const selectedSection = selectedPage?.sections.find((s) => s.id === selectedSectionId) ?? null;

  const getFieldValue = useCallback((sectionId: string, fieldKey: string, defaultValue: string) => {
    return editingFields[sectionId]?.[fieldKey] ?? defaultValue;
  }, [editingFields]);

  const setFieldValue = useCallback((sectionId: string, fieldKey: string, value: string) => {
    setEditingFields((prev) => ({ ...prev, [sectionId]: { ...(prev[sectionId] ?? {}), [fieldKey]: value } }));
    setSavedSections((prev) => { const n = new Set(prev); n.delete(sectionId); return n; });
  }, []);

  const saveSection = useCallback((sectionId: string) => {
    setPages((prev) => prev.map((pg) =>
      pg.id === selectedPageId ? {
        ...pg,
        sections: pg.sections.map((sec) =>
          sec.id === sectionId ? { ...sec, fields: sec.fields.map((f) => ({ ...f, value: editingFields[sectionId]?.[f.key] ?? f.value })) } : sec
        ),
      } : pg
    ));
    setSavedSections((prev) => new Set(prev).add(sectionId));
  }, [selectedPageId, editingFields]);

  const saveAll = useCallback(() => {
    setPages((prev) => prev.map((pg) =>
      pg.id === selectedPageId ? {
        ...pg,
        sections: pg.sections.map((sec) => ({
          ...sec,
          fields: sec.fields.map((f) => ({ ...f, value: editingFields[sec.id]?.[f.key] ?? f.value })),
        })),
      } : pg
    ));
    const allIds = selectedPage?.sections.map((s) => s.id) ?? [];
    setSavedSections(new Set(allIds));
    setSaveAllFlash(true);
    setTimeout(() => setSaveAllFlash(false), 2000);
  }, [selectedPageId, editingFields, selectedPage]);

  const deleteSection = useCallback((sectionId: string) => {
    setPages((prev) => prev.map((pg) =>
      pg.id === selectedPageId ? { ...pg, sections: pg.sections.filter((s) => s.id !== sectionId) } : pg
    ));
    if (selectedSectionId === sectionId) setSelectedSectionId(null);
    setConfirmDelete(null);
  }, [selectedPageId, selectedSectionId]);

  const toggleVisibility = useCallback((sectionId: string) => {
    setPages((prev) => prev.map((pg) =>
      pg.id === selectedPageId ? { ...pg, sections: pg.sections.map((s) => s.id === sectionId ? { ...s, visible: !s.visible } : s) } : pg
    ));
  }, [selectedPageId]);

  const duplicateSection = useCallback((section: PageSection) => {
    const newSection: PageSection = {
      ...section,
      id: `${section.id}_copy_${Date.now()}`,
      name: `${section.name} (Copy)`,
      locked: false,
      fields: section.fields.map((f) => ({ ...f, value: editingFields[section.id]?.[f.key] ?? f.value })),
    };
    setPages((prev) => prev.map((pg) => {
      if (pg.id !== selectedPageId) return pg;
      const idx = pg.sections.findIndex((s) => s.id === section.id);
      const newSections = [...pg.sections];
      newSections.splice(idx + 1, 0, newSection);
      return { ...pg, sections: newSections };
    }));
  }, [selectedPageId, editingFields]);

  const moveSection = useCallback((sectionId: string, direction: 'up' | 'down') => {
    setPages((prev) => prev.map((pg) => {
      if (pg.id !== selectedPageId) return pg;
      const idx = pg.sections.findIndex((s) => s.id === sectionId);
      if (direction === 'up' && idx === 0) return pg;
      if (direction === 'down' && idx === pg.sections.length - 1) return pg;
      const newSections = [...pg.sections];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [newSections[idx], newSections[swapIdx]] = [newSections[swapIdx], newSections[idx]];
      return { ...pg, sections: newSections };
    }));
  }, [selectedPageId]);

  const addSection = useCallback((template: typeof sectionTemplates[0]) => {
    const newSection: PageSection = {
      id: `${template.id}_${Date.now()}`,
      name: template.name,
      description: template.description,
      icon: template.icon,
      visible: true,
      fields: template.fields,
    };
    setPages((prev) => prev.map((pg) =>
      pg.id === selectedPageId ? { ...pg, sections: [...pg.sections, newSection] } : pg
    ));
    setShowAddModal(false);
    setSelectedSectionId(newSection.id);
  }, [selectedPageId]);

  // Drag-to-reorder
  const handleDragStart = (idx: number) => { dragItem.current = idx; };
  const handleDragEnter = (idx: number, sectionId: string) => { dragOverItem.current = idx; setDragOver(sectionId); };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      dragItem.current = null; dragOverItem.current = null; setDragOver(null); return;
    }
    setPages((prev) => prev.map((pg) => {
      if (pg.id !== selectedPageId) return pg;
      const newSections = [...pg.sections];
      const dragged = newSections.splice(dragItem.current!, 1)[0];
      newSections.splice(dragOverItem.current!, 0, dragged);
      return { ...pg, sections: newSections };
    }));
    dragItem.current = null; dragOverItem.current = null; setDragOver(null);
  };

  const hasUnsaved = selectedPage?.sections.some((s) => editingFields[s.id] && Object.keys(editingFields[s.id]).length > 0 && !savedSections.has(s.id));

  return (
    <div className="flex h-full" style={{ minHeight: 0 }}>

      {/* ── Panel 1: Page Tree ── */}
      <div className="flex-shrink-0 flex flex-col" style={{ width: '200px', background: '#FDFAF5', borderRight: '1px solid #E8DFC8', overflowY: 'auto' }}>
        {/* Back */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #EEE5D0' }}>
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#92700A' }}>
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" size={12} color="#92700A" />
            Dashboard
          </button>
        </div>
        {/* Pages */}
        <div className="px-3 py-3 flex-1">
          <p style={{ color: '#C8B880', fontSize: '10px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '4px' }}>Pages</p>
          {pages.map((pg) => (
            <button
              key={pg.id}
              onClick={() => { setSelectedPageId(pg.id); setSelectedSectionId(null); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg mb-0.5 text-left transition-all"
              style={{
                background: selectedPageId === pg.id ? 'rgba(201,168,76,0.14)' : 'transparent',
                color: selectedPageId === pg.id ? '#7A5C08' : '#8A7050',
                borderLeft: selectedPageId === pg.id ? '2px solid #C9A84C' : '2px solid transparent',
                fontSize: '13px',
                fontWeight: selectedPageId === pg.id ? 600 : 400,
              }}
            >
              <Icon path={pg.icon} size={13} color={selectedPageId === pg.id ? '#C9A84C' : '#B8A070'} />
              <span className="truncate">{pg.name}</span>
              <span className="ml-auto text-xs flex-shrink-0" style={{ color: '#C8B880', fontSize: '10px' }}>{pg.sections.length}</span>
            </button>
          ))}
        </div>
        {/* Section list for selected page */}
        {selectedPage && (
          <div className="px-3 pb-3" style={{ borderTop: '1px solid #EEE5D0', paddingTop: '12px' }}>
            <p style={{ color: '#C8B880', fontSize: '10px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '4px' }}>Sections</p>
            {selectedPage.sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => setSelectedSectionId(sec.id)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg mb-0.5 text-left transition-all"
                style={{
                  background: selectedSectionId === sec.id ? 'rgba(201,168,76,0.12)' : 'transparent',
                  color: selectedSectionId === sec.id ? '#7A5C08' : '#9A8060',
                  fontSize: '12px',
                  opacity: sec.visible ? 1 : 0.5,
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sec.visible ? '#16a34a' : '#cbd5e1' }} />
                <span className="truncate">{sec.name}</span>
                {editingFields[sec.id] && Object.keys(editingFields[sec.id]).length > 0 && !savedSections.has(sec.id) && (
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 ml-auto" style={{ background: '#C9A84C' }} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Panel 2: Visual Canvas ── */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#F0EAD8' }}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-2.5 flex-shrink-0" style={{ background: '#FFFFFF', borderBottom: '1px solid #E8DFC8', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          {/* Left: page info */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Icon path={selectedPage?.icon ?? ''} size={14} color="#C9A84C" />
              <span style={{ color: '#7A5C08', fontSize: '13px', fontWeight: 600 }}>{selectedPage?.name}</span>
              <span style={{ color: '#C8B880', fontSize: '12px' }}>·</span>
              <span style={{ color: '#A08040', fontSize: '12px' }}>{selectedPage?.route}</span>
            </div>
            <div className="h-4 w-px" style={{ background: '#E8DFC8' }} />
            <span style={{ color: '#B8A070', fontSize: '12px' }}>{selectedPage?.sections.length ?? 0} sections</span>
          </div>
          {/* Right: actions */}
          <div className="flex items-center gap-2">
            {hasUnsaved && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs" style={{ background: 'rgba(201,168,76,0.12)', color: '#92700A', border: '1px solid rgba(201,168,76,0.3)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#C9A84C' }} />
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.18)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.1)')}
            >
              <Icon path="M12 4v16m8-8H4" size={12} color="#7A5C08" />
              Add Section
            </button>
            {selectedPage && (
              <Link href={selectedPage.route === 'global' ? '/' : selectedPage.route} target="_blank"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{ background: 'rgba(201,168,76,0.08)', color: '#92700A', border: '1px solid rgba(201,168,76,0.2)' }}>
                <Icon path="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" size={12} color="#92700A" />
                Preview
              </Link>
            )}
            <button
              onClick={saveAll}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: saveAllFlash ? '#16a34a' : 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF', transition: 'background 0.3s' }}
            >
              {saveAllFlash ? (
                <><Icon path="M5 13l4 4L19 7" size={12} color="#FFFFFF" strokeWidth={2.5} />Saved!</>
              ) : (
                <><Icon path="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8" size={12} color="#FFFFFF" />Save All</>
              )}
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedPage ? (
            <div className="flex flex-col gap-3 max-w-3xl mx-auto">
              {selectedPage.sections.map((section, idx) => {
                const isSelected = selectedSectionId === section.id;
                const isSaved = savedSections.has(section.id);
                const hasEdits = !!editingFields[section.id] && Object.keys(editingFields[section.id]).length > 0;
                const isDragTarget = dragOver === section.id;

                return (
                  <div
                    key={section.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx, section.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => setSelectedSectionId(section.id)}
                    className="rounded-xl overflow-hidden cursor-pointer transition-all"
                    style={{
                      background: '#FFFFFF',
                      border: isSelected ? '2px solid #C9A84C' : isDragTarget ? '2px dashed #C9A84C' : '1.5px solid #E8DFC8',
                      boxShadow: isSelected ? '0 0 0 3px rgba(201,168,76,0.15), 0 4px 12px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
                      opacity: section.visible ? 1 : 0.55,
                      transform: isDragTarget ? 'scale(1.01)' : 'scale(1)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {/* Section Card Header */}
                    <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: isSelected ? '1px solid #F0E8D5' : 'none' }}>
                      {/* Drag handle */}
                      <div className="flex-shrink-0 cursor-grab active:cursor-grabbing" style={{ color: '#D4C090' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="9" cy="6" r="1.5" fill="currentColor" />
                          <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                          <circle cx="9" cy="12" r="1.5" fill="currentColor" />
                          <circle cx="15" cy="12" r="1.5" fill="currentColor" />
                          <circle cx="9" cy="18" r="1.5" fill="currentColor" />
                          <circle cx="15" cy="18" r="1.5" fill="currentColor" />
                        </svg>
                      </div>
                      {/* Icon */}
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: isSelected ? 'rgba(201,168,76,0.15)' : 'rgba(201,168,76,0.08)' }}>
                        <Icon path={section.icon} size={14} color={isSelected ? '#92700A' : '#C9A84C'} />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p style={{ color: '#2D2010', fontSize: '13px', fontWeight: 600 }}>{section.name}</p>
                          {section.locked && (
                            <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(100,116,139,0.1)', color: '#64748b', fontSize: '10px' }}>Locked</span>
                          )}
                          {hasEdits && !isSaved && (
                            <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(201,168,76,0.15)', color: '#92700A', fontSize: '10px' }}>Unsaved</span>
                          )}
                          {isSaved && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(34,197,94,0.1)', color: '#16a34a', fontSize: '10px' }}>
                              <Icon path="M5 13l4 4L19 7" size={9} color="#16a34a" strokeWidth={2.5} />Saved
                            </span>
                          )}
                        </div>
                        <p style={{ color: '#A08040', fontSize: '11px', marginTop: '1px' }}>{section.description}</p>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {/* Move up */}
                        <button onClick={() => moveSection(section.id, 'up')} disabled={idx === 0}
                          className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                          style={{ background: 'rgba(201,168,76,0.07)', color: idx === 0 ? '#D4C090' : '#92700A', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}
                          title="Move up">
                          <Icon path="M5 15l7-7 7 7" size={12} color={idx === 0 ? '#D4C090' : '#92700A'} />
                        </button>
                        {/* Move down */}
                        <button onClick={() => moveSection(section.id, 'down')} disabled={idx === (selectedPage.sections.length - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                          style={{ background: 'rgba(201,168,76,0.07)', color: idx === selectedPage.sections.length - 1 ? '#D4C090' : '#92700A', cursor: idx === selectedPage.sections.length - 1 ? 'not-allowed' : 'pointer' }}
                          title="Move down">
                          <Icon path="M19 9l-7 7-7-7" size={12} color={idx === selectedPage.sections.length - 1 ? '#D4C090' : '#92700A'} />
                        </button>
                        {/* Duplicate */}
                        <button onClick={() => duplicateSection(section)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                          style={{ background: 'rgba(201,168,76,0.07)', color: '#92700A' }}
                          title="Duplicate section">
                          <Icon path="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" size={12} color="#92700A" />
                        </button>
                        {/* Visibility */}
                        <button onClick={() => toggleVisibility(section.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                          style={{ background: section.visible ? 'rgba(201,168,76,0.07)' : 'rgba(100,116,139,0.1)', color: section.visible ? '#92700A' : '#94a3b8' }}
                          title={section.visible ? 'Hide section' : 'Show section'}>
                          <Icon path={section.visible ? 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z' : 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22'} size={12} color={section.visible ? '#92700A' : '#94a3b8'} />
                        </button>
                        {/* Delete */}
                        {!section.locked && (
                          <button onClick={() => setConfirmDelete(section.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                            style={{ background: 'rgba(220,38,38,0.07)', color: '#dc2626' }}
                            title="Delete section">
                            <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" size={12} color="#dc2626" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Delete confirm inline */}
                    {confirmDelete === section.id && (
                      <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(220,38,38,0.04)', borderTop: '1px solid rgba(220,38,38,0.12)' }}>
                        <p style={{ color: '#2D2010', fontSize: '12px' }}>
                          Delete <strong style={{ color: '#7A5C08' }}>{section.name}</strong>? This cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}>
                            Cancel
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)' }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Quick field preview when selected */}
                    {isSelected && (
                      <div className="px-4 pb-3 pt-1">
                        <p style={{ color: '#B8A070', fontSize: '11px', marginBottom: '6px' }}>
                          {section.fields.length} field{section.fields.length !== 1 ? 's' : ''} · Click a field in the inspector panel →
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {section.fields.slice(0, 6).map((f) => (
                            <span key={f.key} className="px-2 py-0.5 rounded-md text-xs" style={{ background: '#FAF7F2', color: '#92700A', border: '1px solid #E8DFC8', fontSize: '11px' }}>
                              {f.label}
                            </span>
                          ))}
                          {section.fields.length > 6 && (
                            <span className="px-2 py-0.5 rounded-md text-xs" style={{ background: '#FAF7F2', color: '#B8A070', border: '1px solid #E8DFC8', fontSize: '11px' }}>
                              +{section.fields.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add section CTA at bottom */}
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 py-4 rounded-xl transition-all"
                style={{ border: '1.5px dashed #D4C090', color: '#B8A070', background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.color = '#92700A'; e.currentTarget.style.background = 'rgba(201,168,76,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D4C090'; e.currentTarget.style.color = '#B8A070'; e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon path="M12 4v16m8-8H4" size={14} color="currentColor" />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Add New Section</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p style={{ color: '#B8A070', fontSize: '14px' }}>Select a page to edit</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Panel 3: Property Inspector ── */}
      <div className="flex-shrink-0 flex flex-col overflow-hidden" style={{ width: '300px', background: '#FFFFFF', borderLeft: '1px solid #E8DFC8' }}>
        {selectedSection ? (
          <>
            {/* Inspector Header */}
            <div className="px-5 py-3.5 flex-shrink-0" style={{ borderBottom: '1px solid #F0E8D5' }}>
              <div className="flex items-center justify-between mb-0.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)' }}>
                    <Icon path={selectedSection.icon} size={13} color="#92700A" />
                  </div>
                  <div>
                    <p style={{ color: '#2D2010', fontSize: '13px', fontWeight: 700 }}>{selectedSection.name}</p>
                    <p style={{ color: '#A08040', fontSize: '10px' }}>{selectedSection.fields.length} fields</p>
                  </div>
                </div>
                <button onClick={() => setSelectedSectionId(null)} className="w-6 h-6 flex items-center justify-center rounded" style={{ color: '#B8A070' }}>
                  <Icon path="M6 18L18 6M6 6l12 12" size={12} color="#B8A070" />
                </button>
              </div>
            </div>

            {/* Fields */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="flex flex-col gap-4">
                {selectedSection.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      {field.label}
                    </label>
                    <FieldEditor
                      field={field}
                      value={getFieldValue(selectedSection.id, field.key, field.value)}
                      onChange={(v) => setFieldValue(selectedSection.id, field.key, v)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Inspector Footer */}
            <div className="px-4 py-3 flex-shrink-0 flex gap-2" style={{ borderTop: '1px solid #F0E8D5' }}>
              <button
                onClick={() => saveSection(selectedSection.id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF' }}
              >
                <Icon path="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8" size={12} color="#FFFFFF" />
                Save Section
              </button>
              <button
                onClick={() => {
                  setEditingFields((prev) => { const n = { ...prev }; delete n[selectedSection.id]; return n; });
                  setSavedSections((prev) => { const n = new Set(prev); n.delete(selectedSection.id); return n; });
                }}
                className="w-9 flex items-center justify-center rounded-lg transition-all"
                style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#92700A' }}
                title="Reset changes"
              >
                <Icon path="M1 4v6h6M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" size={13} color="#92700A" />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
              <Icon path="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" size={22} color="#C9A84C" />
            </div>
            <div>
              <p style={{ color: '#7A5C08', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Select a Section</p>
              <p style={{ color: '#A08040', fontSize: '12px', lineHeight: 1.5 }}>Click any section on the canvas to edit its content and properties here.</p>
            </div>
            <button onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold mt-2 transition-all"
              style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}>
              <Icon path="M12 4v16m8-8H4" size={12} color="#7A5C08" />
              Add New Section
            </button>
          </div>
        )}
      </div>

      {/* Add Section Modal */}
      {showAddModal && <AddSectionModal onAdd={addSection} onClose={() => setShowAddModal(false)} />}
    </div>
  );
}

// ─── Supported By Manager ─────────────────────────────────────────────────────
interface SupporterItem {
  id: number;
  name: string;
  abbr: string;
  category: string;
  image: string;
}

const CATEGORIES = ['Government', 'Heritage', 'Civic', 'Arts', 'Business', 'Hospitality', 'Culture', 'Festival'];

const initialSupporters: SupporterItem[] = [
  { id: 1, name: 'Madhya Pradesh Tourism', abbr: 'MPT', category: 'Government', image: '' },
  { id: 2, name: 'Ministry of Culture', abbr: 'MoC', category: 'Government', image: '' },
  { id: 3, name: 'Indian Heritage Society', abbr: 'IHS', category: 'Heritage', image: '' },
  { id: 4, name: 'Archaeological Survey', abbr: 'ASI', category: 'Heritage', image: '' },
  { id: 5, name: 'Bhopal Municipal Corp', abbr: 'BMC', category: 'Civic', image: '' },
  { id: 6, name: 'Royal Arts Foundation', abbr: 'RAF', category: 'Arts', image: '' },
  { id: 7, name: 'Indore Chamber', abbr: 'ICC', category: 'Business', image: '' },
  { id: 8, name: 'Heritage Hotels India', abbr: 'HHI', category: 'Hospitality', image: '' },
  { id: 9, name: 'Narmada Cultural Trust', abbr: 'NCT', category: 'Culture', image: '' },
  { id: 10, name: 'Gwalior Arts Council', abbr: 'GAC', category: 'Arts', image: '' },
  { id: 11, name: 'Ujjain Mahotsav', abbr: 'UMF', category: 'Festival', image: '' },
  { id: 12, name: 'Khajuraho Festival', abbr: 'KDF', category: 'Festival', image: '' },
];

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  Government:  { bg: 'rgba(107,26,42,0.12)',  text: '#9B2335', border: 'rgba(107,26,42,0.3)' },
  Heritage:    { bg: 'rgba(26,42,74,0.12)',   text: '#1A2A4A', border: 'rgba(26,42,74,0.3)' },
  Civic:       { bg: 'rgba(26,58,42,0.12)',   text: '#1A3A2A', border: 'rgba(26,58,42,0.3)' },
  Arts:        { bg: 'rgba(58,26,74,0.12)',   text: '#3A1A4A', border: 'rgba(58,26,74,0.3)' },
  Business:    { bg: 'rgba(74,58,26,0.12)',   text: '#4A3A1A', border: 'rgba(74,58,26,0.3)' },
  Hospitality: { bg: 'rgba(74,42,26,0.12)',   text: '#4A2A1A', border: 'rgba(74,42,26,0.3)' },
  Culture:     { bg: 'rgba(26,58,58,0.12)',   text: '#1A3A3A', border: 'rgba(26,58,58,0.3)' },
  Festival:    { bg: 'rgba(201,168,76,0.12)', text: '#7A5C08', border: 'rgba(201,168,76,0.3)' },
};

const emptyForm = { name: '', abbr: '', category: 'Government', image: '' };

function SupportedByManager() {
  const [supporters, setSupporters] = useState<SupporterItem[]>(initialSupporters);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [savedFlash, setSavedFlash] = useState<number | null>(null);
  const [addSavedFlash, setAddSavedFlash] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const inputBase: React.CSSProperties = {
    background: '#FFFFFF',
    border: '1px solid #E0D4B0',
    color: '#2D2010',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    padding: '8px 12px',
    width: '100%',
    transition: 'border-color 0.15s',
  };

  const filteredSupporters = supporters.filter((s) => {
    const matchCat = filterCategory === 'All' || s.category === filterCategory;
    const matchSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.abbr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleAdd = () => {
    if (!form.name.trim() || !form.abbr.trim()) return;
    const newId = Math.max(0, ...supporters.map((s) => s.id)) + 1;
    setSupporters((prev) => [...prev, { id: newId, ...form }]);
    setForm(emptyForm);
    setShowAddForm(false);
    setAddSavedFlash(true);
    setTimeout(() => setAddSavedFlash(false), 2000);
  };

  const handleEditStart = (s: SupporterItem) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, abbr: s.abbr, category: s.category, image: s.image });
  };

  const handleEditSave = (id: number) => {
    if (!editForm.name.trim() || !editForm.abbr.trim()) return;
    setSupporters((prev) => prev.map((s) => s.id === id ? { ...s, ...editForm } : s));
    setEditingId(null);
    setSavedFlash(id);
    setTimeout(() => setSavedFlash(null), 2000);
  };

  const handleDelete = (id: number) => {
    setSupporters((prev) => prev.filter((s) => s.id !== id));
    setDeleteConfirmId(null);
    if (editingId === id) setEditingId(null);
  };

  const categoryCounts = CATEGORIES.reduce<Record<string, number>>((acc, cat) => {
    acc[cat] = supporters.filter((s) => s.category === cat).length;
    return acc;
  }, {});

  return (
    <main className="flex-1 overflow-y-auto p-6" style={{ background: '#F2EDE3' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '22px', fontWeight: 700 }}>
            Supported By
          </h2>
          <p style={{ color: '#A08040', fontSize: '13px', marginTop: '2px' }}>
            Manage supporters, partners, and institutional backers shown on the homepage.
          </p>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setEditingId(null); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
          style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF', boxShadow: '0 2px 8px rgba(201,168,76,0.35)' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          <Icon path="M12 4v16m8-8H4" size={14} color="#FFFFFF" strokeWidth={2.5} />
          Add Supporter
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
          <p style={{ color: '#B8A070', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Total Partners</p>
          <p style={{ color: '#7A5C08', fontSize: '28px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1.1, marginTop: '4px' }}>{supporters.length}</p>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
          <p style={{ color: '#B8A070', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Categories</p>
          <p style={{ color: '#7A5C08', fontSize: '28px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1.1, marginTop: '4px' }}>
            {CATEGORIES.filter((c) => categoryCounts[c] > 0).length}
          </p>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
          <p style={{ color: '#B8A070', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Government</p>
          <p style={{ color: '#7A5C08', fontSize: '28px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1.1, marginTop: '4px' }}>{categoryCounts['Government'] ?? 0}</p>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8' }}>
          <p style={{ color: '#B8A070', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Cultural</p>
          <p style={{ color: '#7A5C08', fontSize: '28px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1.1, marginTop: '4px' }}>
            {(categoryCounts['Heritage'] ?? 0) + (categoryCounts['Culture'] ?? 0) + (categoryCounts['Arts'] ?? 0)}
          </p>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="rounded-2xl p-5 mb-5" style={{ background: '#FFFFFF', border: '2px solid #C9A84C', boxShadow: '0 4px 16px rgba(201,168,76,0.15)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '16px', fontWeight: 700 }}>
              Add New Supporter
            </h3>
            <button onClick={() => { setShowAddForm(false); setForm(emptyForm); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg"
              style={{ background: 'rgba(201,168,76,0.1)', color: '#92700A' }}>
              <Icon path="M6 18L18 6M6 6l12 12" size={13} color="#92700A" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4" style={{ gridTemplateRows: 'auto auto' }}>
            <div>
              <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Organization Name *
              </label>
              <input
                type="text"
                placeholder="e.g. Ministry of Culture"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                style={inputBase}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
              />
            </div>
            <div>
              <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Abbreviation *
              </label>
              <input
                type="text"
                placeholder="e.g. MoC"
                value={form.abbr}
                onChange={(e) => setForm((p) => ({ ...p, abbr: e.target.value }))}
                style={inputBase}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
              />
            </div>
            <div>
              <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Category
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                style={{ ...inputBase, cursor: 'pointer' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Logo / Image
              </label>
              <div className="flex flex-col gap-2">
                {/* Image preview */}
                {form.image && (
                  <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: '#FAF7F2', border: '1px solid #E0D4B0' }}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={form.image} alt="Preview" className="w-12 h-12 rounded-lg object-contain" style={{ background: '#F0E8D5' }} />
                    <span style={{ color: '#7A6040', fontSize: '12px', flex: 1 }} className="truncate">{form.image.startsWith('data:') ? 'Uploaded image' : form.image}</span>
                    <button onClick={() => setForm((p) => ({ ...p, image: '' }))} className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>
                      <Icon path="M6 18L18 6M6 6l12 12" size={11} color="#dc2626" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  {/* Upload button */}
                  <label
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer flex-shrink-0 transition-all"
                    style={{ background: 'rgba(201,168,76,0.1)', border: '1px dashed rgba(201,168,76,0.4)', color: '#92700A', fontSize: '12px', fontWeight: 500 }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)')}
                  >
                    <Icon path="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={14} color="#92700A" />
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => setForm((p) => ({ ...p, image: ev.target?.result as string }));
                        reader.readAsDataURL(file);
                      }
                    }} />
                  </label>
                  {/* URL input */}
                  <input
                    type="url"
                    placeholder="Or paste image URL: https://..."
                    value={form.image.startsWith('data:') ? '' : form.image}
                    onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))}
                    style={{ ...inputBase, flex: 1 }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAdd}
              disabled={!form.name.trim() || !form.abbr.trim()}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: (!form.name.trim() || !form.abbr.trim()) ? '#E8DFC8' : 'linear-gradient(135deg, #C9A84C, #8B6914)',
                color: (!form.name.trim() || !form.abbr.trim()) ? '#B8A070' : '#FFFFFF',
                cursor: (!form.name.trim() || !form.abbr.trim()) ? 'not-allowed' : 'pointer',
              }}
            >
              <Icon path="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8" size={13} color={(!form.name.trim() || !form.abbr.trim()) ? '#B8A070' : '#FFFFFF'} />
              Save Supporter
            </button>
            <button
              onClick={() => { setShowAddForm(false); setForm(emptyForm); }}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}
            >
              Cancel
            </button>
            {addSavedFlash && (
              <span className="flex items-center gap-1.5 text-sm" style={{ color: '#16a34a' }}>
                <Icon path="M5 13l4 4L19 7" size={14} color="#16a34a" strokeWidth={2.5} />
                Supporter added!
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filter & Search Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[200px]" style={{ background: '#FFFFFF', border: '1px solid #E0D4B0', maxWidth: '280px' }}>
          <Icon path="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={14} color="#B8A070" />
          <input
            type="text"
            placeholder="Search supporters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent outline-none"
            style={{ color: '#2D2010', fontSize: '13px' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ color: '#B8A070' }}>
              <Icon path="M6 18L18 6M6 6l12 12" size={12} color="#B8A070" />
            </button>
          )}
        </div>
        {/* Category filters */}
        <div className="flex flex-wrap gap-1.5">
          {['All', ...CATEGORIES].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                background: filterCategory === cat ? 'linear-gradient(135deg, #C9A84C, #8B6914)' : '#FFFFFF',
                color: filterCategory === cat ? '#FFFFFF' : '#7A6040',
                border: filterCategory === cat ? 'none' : '1px solid #E0D4B0',
              }}
            >
              {cat}
              {cat !== 'All' && categoryCounts[cat] > 0 && (
                <span className="ml-1 opacity-75">({categoryCounts[cat]})</span>
              )}
            </button>
          ))}
        </div>
        <span style={{ color: '#B8A070', fontSize: '12px', marginLeft: 'auto' }}>
          {filteredSupporters.length} of {supporters.length} shown
        </span>
      </div>

      {/* Supporters Table */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        {/* Table Header */}
        <div className="grid px-5 py-3" style={{ gridTemplateColumns: '48px 1fr 80px 140px 80px 120px', borderBottom: '1px solid #F0E8D5', gap: '12px' }}>
          {['Image', 'Organization', 'Abbr', 'Category', 'ID', 'Actions'].map((h) => (
            <span key={h} className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#B8A070' }}>{h}</span>
          ))}
        </div>

        {/* Rows */}
        {filteredSupporters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
              <Icon path="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" size={22} color="#C9A84C" />
            </div>
            <p style={{ color: '#A08040', fontSize: '14px' }}>No supporters found</p>
            <button onClick={() => { setFilterCategory('All'); setSearchQuery(''); }} style={{ color: '#C9A84C', fontSize: '12px' }}>Clear filters</button>
          </div>
        ) : (
          filteredSupporters.map((s, idx) => {
            const isEditing = editingId === s.id;
            const isDeleteConfirm = deleteConfirmId === s.id;
            const isSaved = savedFlash === s.id;
            const cfg = categoryColors[s.category] ?? categoryColors.Festival;

            return (
              <div key={s.id} style={{ borderBottom: idx < filteredSupporters.length - 1 ? '1px solid #F8F3EA' : 'none' }}>
                {/* Normal / Edit Row */}
                <div
                  className="grid px-5 py-3.5 items-center transition-all"
                  style={{
                    gridTemplateColumns: '48px 1fr 80px 140px 80px 120px',
                    gap: '12px',
                    background: isEditing ? 'rgba(201,168,76,0.04)' : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!isEditing) e.currentTarget.style.background = 'rgba(201,168,76,0.03)'; }}
                  onMouseLeave={(e) => { if (!isEditing) e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Image */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                    {s.image && !isEditing ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={s.image} alt={`${s.name} logo`} className="w-full h-full object-contain p-1" />
                    ) : !isEditing ? (
                      <span className="text-xs font-bold" style={{ color: cfg.text, fontFamily: 'DM Sans, sans-serif' }}>{s.abbr.slice(0, 3)}</span>
                    ) : null}
                    {isEditing && (
                      <label className="w-full h-full flex items-center justify-center cursor-pointer" title="Upload image">
                        {editForm.image ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={editForm.image} alt="logo" className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <Icon path="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={14} color={cfg.text} />
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setEditForm((p) => ({ ...p, image: ev.target?.result as string }));
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </label>
                    )}
                  </div>

                  {/* Name */}
                  <div className="min-w-0">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        style={{ ...inputBase, padding: '6px 10px', fontSize: '13px' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
                      />
                    ) : (
                      <p style={{ color: '#2D2010', fontSize: '13px', fontWeight: 500 }}>{s.name}</p>
                    )}
                  </div>

                  {/* Abbr */}
                  <div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.abbr}
                        onChange={(e) => setEditForm((p) => ({ ...p, abbr: e.target.value }))}
                        style={{ ...inputBase, padding: '6px 10px', fontSize: '12px' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
                      />
                    ) : (
                      <span style={{ color: '#7A6040', fontSize: '12px', fontWeight: 600 }}>{s.abbr}</span>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    {isEditing ? (
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value }))}
                        style={{ ...inputBase, padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
                      >
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <span
                        className="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
                      >
                        {s.category}
                      </span>
                    )}
                  </div>

                  {/* ID */}
                  <span style={{ color: '#C8B880', fontSize: '11px' }}>#{s.id}</span>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => handleEditSave(s.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
                          style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF' }}
                        >
                          <Icon path="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8" size={11} color="#FFFFFF" />
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingId(null); }}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-medium"
                          style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {isSaved && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: '#16a34a' }}>
                            <Icon path="M5 13l4 4L19 7" size={11} color="#16a34a" strokeWidth={2.5} />
                            Saved
                          </span>
                        )}
                        {!isSaved && (
                          <>
                            <button
                              onClick={() => handleEditStart(s)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                              style={{ background: 'rgba(201,168,76,0.1)', color: '#92700A' }}
                              title="Edit"
                            >
                              <Icon path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" size={12} color="#92700A" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(s.id)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                              style={{ background: 'rgba(220,38,38,0.07)', color: '#dc2626' }}
                              title="Delete"
                            >
                              <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" size={12} color="#dc2626" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Image URL edit row — shown when editing */}
                {isEditing && (
                  <div className="px-5 pb-3" style={{ background: 'rgba(201,168,76,0.04)' }}>
                    <label className="block mb-1.5" style={{ color: '#92700A', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      Image URL
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="url"
                        placeholder="https://example.com/logo.png"
                        value={editForm.image.startsWith('data:') ? '' : editForm.image}
                        onChange={(e) => setEditForm((p) => ({ ...p, image: e.target.value }))}
                        style={{ ...inputBase, padding: '6px 10px', fontSize: '12px', flex: 1 }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#E0D4B0')}
                      />
                      {editForm.image && (
                        <button onClick={() => setEditForm((p) => ({ ...p, image: '' }))} className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>
                          <Icon path="M6 18L18 6M6 6l12 12" size={12} color="#dc2626" />
                        </button>
                      )}
                    </div>
                    <p style={{ color: '#C8B880', fontSize: '10px', marginTop: '4px' }}>Click the image thumbnail on the left to upload a file, or paste a URL above.</p>
                  </div>
                )}

                {/* Delete Confirm Row */}
                {isDeleteConfirm && (
                  <div className="px-5 py-3 flex items-center justify-between" style={{ background: 'rgba(220,38,38,0.04)', borderTop: '1px solid rgba(220,38,38,0.1)' }}>
                    <p style={{ color: '#2D2010', fontSize: '12px' }}>
                      Remove <strong style={{ color: '#7A5C08' }}>{s.name}</strong> from supporters? This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ background: 'rgba(201,168,76,0.1)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.3)' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer hint */}
      <p className="text-center mt-4" style={{ color: '#C8B880', fontSize: '11px' }}>
        Changes are reflected in the Supported By section on the homepage.
      </p>
    </main>
  );
}

// ─── Sidebar Items ────────────────────────────────────────────────────────────
const sidebarItems = [
  { label: 'Overview',   icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { label: 'Events',     icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { label: 'Members',    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { label: 'Revenue',    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { label: 'Venues',     icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
  { label: 'Reports',    icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { label: 'Settings',   icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'events' | 'members'>('events');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'pages' | 'supported_by'>('overview');
  const [pagesDropdownOpen, setPagesDropdownOpen] = useState(false);
  const [initialPages] = useState<AppPage[]>(buildInitialPages);

  return (
    <div className="min-h-screen flex" style={{ background: '#F2EDE3', fontFamily: 'DM Sans, sans-serif' }}>

      {/* ── Sidebar ── */}
      <aside className="flex-shrink-0 flex flex-col transition-all duration-300"
        style={{ width: sidebarCollapsed ? '64px' : '220px', background: '#FFFFFF', borderRight: '1px solid #E8DFC8', minHeight: '100vh', boxShadow: '2px 0 8px rgba(0,0,0,0.06)' }}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-4" style={{ borderBottom: '1px solid #F0E8D5', height: '64px' }}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M3 18h18M5 18l2-8 5 4 5-6 2 10" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="5" cy="9" r="1.5" fill="#FFFFFF" />
                  <circle cx="12" cy="6" r="1.5" fill="#FFFFFF" />
                  <circle cx="19" cy="9" r="1.5" fill="#FFFFFF" />
                </svg>
              </div>
              <span style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '14px', fontWeight: 600 }}>Admin</span>
            </div>
          )}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-7 h-7 flex items-center justify-center rounded" style={{ color: '#92700A', background: 'rgba(201,168,76,0.1)' }}>
            <Icon path={sidebarCollapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} size={14} color="#92700A" />
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 flex flex-col gap-1 px-2">
          {/* Overview */}
          <button onClick={() => setActiveSection('overview')}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all duration-150"
            style={{ background: activeSection === 'overview' ? 'rgba(201,168,76,0.12)' : 'transparent', color: activeSection === 'overview' ? '#7A5C08' : '#8A7050', borderLeft: activeSection === 'overview' ? '2px solid #C9A84C' : '2px solid transparent' }}>
            <Icon path={sidebarItems[0].icon} size={17} color={activeSection === 'overview' ? '#C9A84C' : '#B8A070'} />
            {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: activeSection === 'overview' ? 600 : 400 }}>Overview</span>}
          </button>

          {/* Pages Dropdown */}
          <div>
            <button
              onClick={() => { setPagesDropdownOpen(!pagesDropdownOpen); if (sidebarCollapsed) setSidebarCollapsed(false); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all duration-150"
              style={{ background: activeSection === 'pages' ? 'rgba(201,168,76,0.12)' : pagesDropdownOpen ? 'rgba(201,168,76,0.07)' : 'transparent', color: activeSection === 'pages' || pagesDropdownOpen ? '#7A5C08' : '#8A7050', borderLeft: activeSection === 'pages' ? '2px solid #C9A84C' : '2px solid transparent' }}>
              <Icon path="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" size={17} color={activeSection === 'pages' ? '#C9A84C' : '#B8A070'} />
              {!sidebarCollapsed && (
                <>
                  <span style={{ fontSize: '13px', fontWeight: 400, flex: 1 }}>Pages</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ transition: 'transform 0.2s', transform: pagesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
            {pagesDropdownOpen && !sidebarCollapsed && (
              <div className="ml-3 mt-1 flex flex-col gap-0.5" style={{ borderLeft: '1px solid #E8DFC8', paddingLeft: '12px' }}>
                {initialPages.map((pg) => (
                  <button key={pg.id} onClick={() => setActiveSection('pages')}
                    className="flex items-center gap-2 px-2 py-2 rounded-lg w-full text-left transition-all"
                    style={{ color: activeSection === 'pages' ? '#7A5C08' : '#9A8060', fontSize: '12px' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#7A5C08')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = activeSection === 'pages' ? '#7A5C08' : '#9A8060')}>
                    <Icon path={pg.icon} size={12} color="#C9A84C" />
                    <span className="truncate">{pg.name}</span>
                    <span style={{ color: '#C8B880', fontSize: '10px', marginLeft: 'auto', flexShrink: 0 }}>{pg.sections.length}</span>
                  </button>
                ))}
                <button onClick={() => setActiveSection('pages')}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg w-full text-left transition-all mt-1"
                  style={{ color: '#92700A', fontSize: '11px', background: 'rgba(201,168,76,0.07)', border: '1px dashed rgba(201,168,76,0.35)' }}>
                  <Icon path="M12 5v14M5 12h14" size={11} color="#92700A" />
                  Manage All Pages
                </button>
              </div>
            )}
          </div>

          {/* Supported By */}
          <button onClick={() => setActiveSection('supported_by')}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all duration-150"
            style={{ background: activeSection === 'supported_by' ? 'rgba(201,168,76,0.12)' : 'transparent', color: activeSection === 'supported_by' ? '#7A5C08' : '#8A7050', borderLeft: activeSection === 'supported_by' ? '2px solid #C9A84C' : '2px solid transparent' }}>
            <Icon path="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" size={17} color={activeSection === 'supported_by' ? '#C9A84C' : '#B8A070'} />
            {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: activeSection === 'supported_by' ? 600 : 400 }}>Supported By</span>}
          </button>

          {/* Visual Builder */}
          <Link href="/builder"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all duration-150"
            style={{ color: '#8A7050', borderLeft: '2px solid transparent' }}>
            <Icon path="M4 5h16v14H4zM4 9h16M9 9v10" size={17} color="#B8A070" />
            {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: 400 }}>Website Builder</span>}
          </Link>

          {/* Other sidebar items */}
          {sidebarItems.slice(1).map((item) => (
            <button key={item.label}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all duration-150"
              style={{ background: 'transparent', color: '#8A7050', borderLeft: '2px solid transparent' }}>
              <Icon path={item.icon} size={17} color="#B8A070" />
              {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: 400 }}>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Back to Site */}
        <div className="px-2 pb-4">
          <Link href="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-all duration-150" style={{ color: '#9A8060', fontSize: '13px' }}>
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" size={17} color="#B8A070" />
            {!sidebarCollapsed && <span>Back to Site</span>}
          </Link>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="flex items-center justify-between px-6 flex-shrink-0"
          style={{ height: '64px', background: '#FFFFFF', borderBottom: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div>
            <h1 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '20px', fontWeight: 700, lineHeight: 1 }}>
              {activeSection === 'pages' ? 'Page Editor' : activeSection === 'supported_by' ? 'Supported By' : 'Admin Dashboard'}
            </h1>
            <p style={{ color: '#A08040', fontSize: '12px', marginTop: '2px' }}>
              {activeSection === 'pages' ? 'Visual editor — select sections to edit properties' : activeSection === 'supported_by' ? 'Add, edit, and remove supporters & partners' : 'Rani Kamlapati Heritage Platform'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative w-9 h-9 flex items-center justify-center rounded-lg" style={{ background: 'rgba(201,168,76,0.1)' }}>
              <Icon path="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" size={18} color="#92700A" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: '#dc2626' }} />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF' }}>RK</div>
              <div className="hidden md:block">
                <p style={{ color: '#2D2010', fontSize: '13px', fontWeight: 600 }}>Admin</p>
                <p style={{ color: '#A08040', fontSize: '11px' }}>Super Admin</p>
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        {activeSection === 'pages' ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            <AdvancedPageEditor pages={initialPages} onBack={() => setActiveSection('overview')} />
          </div>
        ) : activeSection === 'supported_by' ? (
          <SupportedByManager />
        ) : (
          <main className="flex-1 overflow-y-auto p-6" style={{ background: '#F2EDE3' }}>

            {/* Stats Bento Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-xl p-5 flex flex-col gap-3 relative overflow-hidden"
                  style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div className="absolute top-0 right-0 w-16 h-16 rounded-bl-full" style={{ background: stat.accent, opacity: 0.08 }} />
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>{stat.icon}</div>
                    <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: stat.positive ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: stat.positive ? '#16a34a' : '#dc2626' }}>
                      {stat.positive ? '↑' : '↓'}
                    </span>
                  </div>
                  <div>
                    <p style={{ color: '#7A5C08', fontSize: '26px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif', lineHeight: 1 }}>{stat.value}</p>
                    <p style={{ color: '#8A7050', fontSize: '12px', marginTop: '4px' }}>{stat.label}</p>
                    <p style={{ color: stat.positive ? '#16a34a' : '#dc2626', fontSize: '11px', marginTop: '2px' }}>{stat.change}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Main Bento: Table + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div className="flex items-center justify-between px-5 pt-4 pb-0">
                  <div className="flex gap-1">
                    {(['events', 'members'] as const).map((tab) => (
                      <button key={tab} onClick={() => setActiveTab(tab)}
                        className="px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-150 capitalize"
                        style={{ background: activeTab === tab ? 'rgba(201,168,76,0.1)' : 'transparent', color: activeTab === tab ? '#7A5C08' : '#9A8060', borderBottom: activeTab === tab ? '2px solid #C9A84C' : '2px solid transparent' }}>
                        {tab}
                      </button>
                    ))}
                  </div>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all" style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FFFFFF' }}>
                    <Icon path="M12 5v14M5 12h14" size={12} color="#FFFFFF" strokeWidth={2.5} />
                    Add {activeTab === 'events' ? 'Event' : 'Member'}
                  </button>
                </div>
                <div style={{ height: '1px', background: '#F0E8D5', margin: '0 20px' }} />
                <div className="overflow-x-auto">
                  {activeTab === 'events' ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: '1px solid #F0E8D5' }}>
                          {['Event', 'Date', 'Venue', 'Status', 'Tickets', 'Revenue'].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#B8A070' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((ev, i) => (
                          <tr key={ev.id} style={{ borderBottom: i < events.length - 1 ? '1px solid #F8F3EA' : 'none', transition: 'background 0.15s' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.04)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                            <td className="px-5 py-3.5"><span style={{ color: '#2D2010', fontWeight: 500, fontSize: '13px' }}>{ev.title}</span></td>
                            <td className="px-5 py-3.5"><span style={{ color: '#7A6040', fontSize: '12px' }}>{ev.date}</span></td>
                            <td className="px-5 py-3.5"><span style={{ color: '#7A6040', fontSize: '12px' }}>{ev.venue}</span></td>
                            <td className="px-5 py-3.5"><StatusBadge status={ev.status} /></td>
                            <td className="px-5 py-3.5"><span style={{ color: '#5A4010', fontSize: '13px' }}>{ev.tickets.toLocaleString()}</span></td>
                            <td className="px-5 py-3.5"><span style={{ color: '#7A5C08', fontSize: '13px', fontWeight: 600 }}>{ev.revenue}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: '1px solid #F0E8D5' }}>
                          {['Name', 'Role', 'Joined', 'Tier', 'Status'].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#B8A070' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, i) => (
                          <tr key={m.id} style={{ borderBottom: i < members.length - 1 ? '1px solid #F8F3EA' : 'none', transition: 'background 0.15s' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.04)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)', color: '#7A5C08' }}>
                                  {m.name.split(' ').map((n) => n[0]).join('')}
                                </div>
                                <span style={{ color: '#2D2010', fontWeight: 500, fontSize: '13px' }}>{m.name}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3.5"><span style={{ color: '#7A6040', fontSize: '12px' }}>{m.role}</span></td>
                            <td className="px-5 py-3.5"><span style={{ color: '#7A6040', fontSize: '12px' }}>{m.joined}</span></td>
                            <td className="px-5 py-3.5"><TierBadge tier={m.tier} /></td>
                            <td className="px-5 py-3.5"><StatusBadge status={m.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="rounded-xl p-5 flex flex-col" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '16px', fontWeight: 600 }}>Recent Activity</h3>
                  <span style={{ color: '#B8A070', fontSize: '11px' }}>Today</span>
                </div>
                <div className="flex flex-col gap-4 flex-1">
                  {activities.map((act) => (
                    <div key={act.id} className="flex items-start gap-3">
                      <ActivityIcon type={act.type} />
                      <div className="flex-1 min-w-0">
                        <p style={{ color: '#2D2010', fontSize: '12px', fontWeight: 500, lineHeight: 1.4 }}>{act.action}</p>
                        <p className="line-clamp-2" style={{ color: '#8A7050', fontSize: '11px', marginTop: '2px' }}>{act.subject}</p>
                        <p style={{ color: '#C8B880', fontSize: '10px', marginTop: '3px' }}>{act.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="mt-4 w-full py-2 rounded-lg text-xs font-medium transition-all" style={{ background: 'rgba(201,168,76,0.08)', color: '#7A5C08', border: '1px solid rgba(201,168,76,0.25)' }}>
                  View All Activity
                </button>
              </div>
            </div>

            {/* Bottom Bento */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Quick Actions */}
              <div className="rounded-xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Quick Actions</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'New Event', icon: 'M12 5v14M5 12h14', color: '#C9A84C' },
                    { label: 'Add Member', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', color: '#B8860B' },
                    { label: 'Send Notice', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', color: '#8B6914' },
                    { label: 'Export Data', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', color: '#9B2335' },
                  ].map((action) => (
                    <button key={action.label}
                      className="flex flex-col items-center gap-2 p-3 rounded-lg transition-all duration-150"
                      style={{ background: '#FAF7F2', border: '1px solid #E8DFC8' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F5EED8')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#FAF7F2')}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${action.color}18` }}>
                        <Icon path={action.icon} size={16} color={action.color} strokeWidth={2} />
                      </div>
                      <span style={{ color: '#5A4010', fontSize: '11px', fontWeight: 500 }}>{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="rounded-xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Revenue Breakdown</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { label: 'Ticket Sales', amount: '₹18.2L', pct: 74 },
                    { label: 'Memberships', amount: '₹4.1L', pct: 17 },
                    { label: 'Sponsorships', amount: '₹1.8L', pct: 7 },
                    { label: 'Merchandise', amount: '₹0.6L', pct: 2 },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="flex justify-between mb-1">
                        <span style={{ color: '#7A6040', fontSize: '12px' }}>{item.label}</span>
                        <span style={{ color: '#7A5C08', fontSize: '12px', fontWeight: 600 }}>{item.amount}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F0E8D5' }}>
                        <div className="h-full rounded-full" style={{ width: `${item.pct}%`, background: 'linear-gradient(to right, #8B6914, #C9A84C)' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid #F0E8D5' }}>
                  <div className="flex justify-between">
                    <span style={{ color: '#9A8060', fontSize: '12px' }}>Total Revenue</span>
                    <span style={{ color: '#5A4010', fontSize: '14px', fontWeight: 700, fontFamily: '"Playfair Display", Georgia, serif' }}>₹24.7L</span>
                  </div>
                </div>
              </div>

              {/* Platform Health */}
              <div className="rounded-xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E8DFC8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#7A5C08', fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Platform Health</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { label: 'Server Uptime', value: '99.98%', ok: true },
                    { label: 'Avg. Load Time', value: '1.2s', ok: true },
                    { label: 'Pending Approvals', value: '7', ok: false },
                    { label: 'Open Support Tickets', value: '3', ok: true },
                    { label: 'Last Backup', value: '3 hrs ago', ok: true },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.ok ? '#16a34a' : '#f59e0b' }} />
                        <span style={{ color: '#7A6040', fontSize: '12px' }}>{item.label}</span>
                      </div>
                      <span style={{ color: item.ok ? '#5A4010' : '#d97706', fontSize: '12px', fontWeight: 600 }}>{item.value}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)' }}>
                  <p style={{ color: '#16a34a', fontSize: '11px', fontWeight: 500 }}>✓ All systems operational</p>
                </div>
              </div>
            </div>
          </main>
        )}
      </div>
    </div>
  );
}